import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Video, Home } from "lucide-react";
import { BRAND, DOCTOR, whatsappUrl, teleconsultUrl } from "@/lib/constants";

const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-14 lg:pt-20 pb-24 lg:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <motion.div
              variants={fade}
              initial="hidden"
              animate="show"
              custom={0}
              className="flex items-center gap-4 mb-8"
            >
              <span className="h-px w-12 bg-[#B08A4A]" />
              <span className="font-mono text-[10px] uppercase tracking-widest-plus text-[#475569]">
                Chief Pathologist · Diagnostic Excellence
              </span>
            </motion.div>

            <motion.h1
              variants={fade}
              initial="hidden"
              animate="show"
              custom={1}
              className="font-serif text-5xl sm:text-6xl lg:text-[5.25rem] leading-[1.02] tracking-tight text-[#0A2540]"
            >
              {BRAND.name}
            </motion.h1>

            <motion.div
              variants={fade}
              initial="hidden"
              animate="show"
              custom={2}
              className="mt-6 flex flex-wrap items-baseline gap-x-4 gap-y-2"
            >
              <span className="font-serif text-2xl lg:text-3xl text-[#0A2540]">
                {DOCTOR.name},
              </span>
              <span className="font-sans text-sm lg:text-base text-[#475569] tracking-wide">
                MBBS, MD Pathology
                <span className="text-[#B08A4A] font-medium ml-1">
                  (Gold Medalist)
                </span>
              </span>
            </motion.div>

            <motion.p
              variants={fade}
              initial="hidden"
              animate="show"
              custom={3}
              className="mt-8 max-w-2xl font-serif italic text-2xl lg:text-3xl text-[#0A2540]/85 leading-snug"
            >
              {BRAND.tagline}
            </motion.p>

            <motion.p
              variants={fade}
              initial="hidden"
              animate="show"
              custom={4}
              className="mt-6 max-w-xl font-sans text-base text-[#475569] leading-relaxed"
            >
              A boutique diagnostics practice pairing tertiary-care rigour with
              patient-centred consultation. Advanced pathology, cytology,
              FNAC, and OPD services — reviewed personally by a Gold Medalist
              consultant.
            </motion.p>

            <motion.div
              variants={fade}
              initial="hidden"
              animate="show"
              custom={5}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <a
                href={teleconsultUrl()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-book-btn"
                className="group btn-primary"
              >
                <Video size={16} strokeWidth={1.5} />
                Book Online Teleconsultation
                <ArrowUpRight
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  size={16}
                  strokeWidth={1.5}
                />
              </a>
              <Link
                to="/b2b"
                data-testid="hero-b2b-btn"
                className="group btn-secondary"
              >
                Explore B2B &amp; Research Solutions
                <ArrowUpRight
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  size={16}
                  strokeWidth={1.5}
                />
              </Link>
            </motion.div>

            <motion.div
              variants={fade}
              initial="hidden"
              animate="show"
              custom={5}
              className="mt-4"
            >
              <a
                href={whatsappUrl()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-labwork-link"
                className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest-plus text-[#475569] hover:text-[#0A2540] transition-colors"
              >
                <Home size={12} strokeWidth={1.5} />
                Or book lab tests &amp; home sample collection
                <ArrowUpRight size={12} strokeWidth={1.5} />
              </a>
            </motion.div>

            <motion.div
              variants={fade}
              initial="hidden"
              animate="show"
              custom={6}
              className="mt-14 grid grid-cols-3 gap-6 lg:gap-10 max-w-lg"
            >
              {[
                { k: "Experience", v: DOCTOR.yearsExperience, s: "years" },
                { k: "Publications", v: DOCTOR.papersPublished, s: "papers" },
                { k: "Cases Reviewed", v: DOCTOR.cases, s: "" },
              ].map((s) => (
                <div key={s.k} className="border-t border-[#B08A4A]/60 pt-4">
                  <div className="font-serif text-3xl lg:text-4xl text-[#0A2540]">
                    {s.v}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#475569] mt-2">
                    {s.k} {s.s && `· ${s.s}`}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            variants={fade}
            initial="hidden"
            animate="show"
            custom={3}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] w-full max-w-md mx-auto">
              <div className="absolute inset-0 border border-[#B08A4A]/60 translate-x-3 translate-y-3" />
              <div className="relative w-full h-full overflow-hidden bg-[#F1F5F9]">
                <img
                  src={DOCTOR.photo}
                  alt={DOCTOR.name}
                  className="w-full h-full object-cover"
                  data-testid="hero-portrait"
                  loading="eager"
                />
                <div className="absolute inset-0 border border-[#0A2540]/10 pointer-events-none" />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white border border-[#E2E8F0] px-5 py-3 glow-navy">
                <div className="font-mono text-[9px] uppercase tracking-widest-plus text-[#94A3B8]">
                  Verified Consultant
                </div>
                <div className="font-serif text-base text-[#0A2540]">
                  MD · Pathology
                </div>
              </div>
              <div className="absolute -top-4 right-4">
                <div className="bg-[#B08A4A] text-white px-4 py-2 font-mono text-[9px] uppercase tracking-widest-plus">
                  Gold Medalist
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="rule-nav" />
    </section>
  );
};

export default Hero;
